import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiRequest from '../../api/request';

// Interval Community CRUD — backend at /interval-community
// Notes that shaped this slice:
//  - POST and PUT ALWAYS take an array under `data`, even for a single record.
//  - PUT carries `community_id` (a JSON number, not a string) in the BODY.
//  - PUT and DELETE return no record — callers must refetch after mutating.
//  - Standard portal envelope { status, message, data, meta }; `meta` only on list.
//  - The class -> "needs a syllabus split" rule lives on the server. The pre-data
//    endpoint returns each class already flagged, so the UI never hard codes ids.

const initialState = {
  // list endpoint (paginated)
  table: {
    loading: false,
    data: [],
    meta: {},
  },
  // single community (get one)
  single: {
    loading: false,
    data: null,
  },
  // classes + the syllabuses allowed to hold their own link
  preData: {
    loading: false,
    classes: [],
    syllabuses: [],
  },
  // shared flag for create / update / delete
  mutating: false,
};

// 1. LIST ------------------------------------------------------------------
export const getIntervalCommunities = createAsyncThunk(
  'intervalCommunity/getIntervalCommunities',
  async (params = {}) => {
    const { page = 1, limit = 10, search = '', sortBy, sortDirection } = params;

    const searchParams = new URLSearchParams({
      page,
      limit,
      search: search ?? '',
    });
    if (sortBy) {
      searchParams.append('sortBy', sortBy);
      searchParams.append('sortDirection', sortDirection || 'asc');
    }

    const res = await apiRequest({
      method: 'get',
      url: `interval-community?${searchParams.toString()}`,
    });
    return res;
  },
);

// 2. GET ONE ---------------------------------------------------------------
export const getIntervalCommunity = createAsyncThunk(
  'intervalCommunity/getIntervalCommunity',
  async (communityId) => {
    const res = await apiRequest({
      method: 'get',
      url: `interval-community/${communityId}`,
    });
    return res;
  },
);

// 3. PRE DATA (classes + allowed syllabuses for the form) ------------------
export const getIntervalCommunityPreData = createAsyncThunk(
  'intervalCommunity/getIntervalCommunityPreData',
  async () => {
    const res = await apiRequest({
      method: 'get',
      url: 'interval-community/pre-data',
    });
    return res;
  },
);

// 4. CREATE (array payload, all-or-nothing) --------------------------------
export const createIntervalCommunities = createAsyncThunk(
  'intervalCommunity/createIntervalCommunities',
  async (data) => {
    const res = await apiRequest({
      method: 'post',
      url: 'interval-community',
      data: { data: Array.isArray(data) ? data : [data] },
    });
    return res;
  },
);

// 5. UPDATE (array payload, community_id in body as a number) --------------
export const updateIntervalCommunities = createAsyncThunk(
  'intervalCommunity/updateIntervalCommunities',
  async (data) => {
    const list = (Array.isArray(data) ? data : [data]).map((item) => ({
      ...item,
      community_id: Number(item.community_id), // API rejects a string id
    }));
    const res = await apiRequest({
      method: 'put',
      url: 'interval-community',
      data: { data: list },
    });
    return res;
  },
);

// 6. DELETE (soft delete, id in URL) ---------------------------------------
export const deleteIntervalCommunity = createAsyncThunk(
  'intervalCommunity/deleteIntervalCommunity',
  async (communityId) => {
    const res = await apiRequest({
      method: 'delete',
      url: `interval-community/${communityId}`,
    });
    return res;
  },
);

const intervalCommunitySlice = createSlice({
  name: 'intervalCommunity',
  initialState,
  reducers: {
    clearIntervalCommunity: (state) => {
      state.single = { loading: false, data: null };
    },
  },
  extraReducers: (builder) => {
    // ---------------- LIST ----------------
    builder.addCase(getIntervalCommunities.pending, (state) => {
      state.table.loading = true;
    });
    builder.addCase(getIntervalCommunities.fulfilled, (state, action) => {
      state.table.loading = false;
      state.table.data = action.payload?.data || [];
      state.table.meta = action.payload?.meta || {};
    });
    builder.addCase(getIntervalCommunities.rejected, (state) => {
      state.table.loading = false;
    });

    // ---------------- GET ONE ----------------
    builder.addCase(getIntervalCommunity.pending, (state) => {
      state.single.loading = true;
    });
    builder.addCase(getIntervalCommunity.fulfilled, (state, action) => {
      state.single.loading = false;
      state.single.data = action.payload?.data || null;
    });
    builder.addCase(getIntervalCommunity.rejected, (state) => {
      state.single.loading = false;
    });

    // ---------------- PRE DATA ----------------
    builder.addCase(getIntervalCommunityPreData.pending, (state) => {
      state.preData.loading = true;
    });
    builder.addCase(getIntervalCommunityPreData.fulfilled, (state, action) => {
      state.preData.loading = false;
      state.preData.classes = action.payload?.data?.classes || [];
      state.preData.syllabuses = action.payload?.data?.syllabuses || [];
    });
    builder.addCase(getIntervalCommunityPreData.rejected, (state) => {
      state.preData.loading = false;
      state.preData.classes = [];
      state.preData.syllabuses = [];
    });

    // ---------------- CREATE ----------------
    builder.addCase(createIntervalCommunities.pending, (state) => {
      state.mutating = true;
    });
    builder.addCase(createIntervalCommunities.fulfilled, (state) => {
      state.mutating = false;
    });
    builder.addCase(createIntervalCommunities.rejected, (state) => {
      state.mutating = false;
    });

    // ---------------- UPDATE ----------------
    builder.addCase(updateIntervalCommunities.pending, (state) => {
      state.mutating = true;
    });
    builder.addCase(updateIntervalCommunities.fulfilled, (state) => {
      state.mutating = false;
    });
    builder.addCase(updateIntervalCommunities.rejected, (state) => {
      state.mutating = false;
    });

    // ---------------- DELETE ----------------
    builder.addCase(deleteIntervalCommunity.pending, (state) => {
      state.mutating = true;
    });
    builder.addCase(deleteIntervalCommunity.fulfilled, (state) => {
      state.mutating = false;
    });
    builder.addCase(deleteIntervalCommunity.rejected, (state) => {
      state.mutating = false;
    });
  },
});

export const { clearIntervalCommunity } = intervalCommunitySlice.actions;
export default intervalCommunitySlice.reducer;
